import { Radio, type RadioSize } from '@trustoo/components/Radio/v2'
import React, { useState } from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

const RADIO_USAGE = `import { Radio } from '@trustoo/components/Radio/v2'

// A native radio group: share a name, control the selected value
<Radio name="plan" value="basic" checked={plan === 'basic'}
       onChange={() => setPlan('basic')} label="Basic plan" />
<Radio name="plan" value="pro" checked={plan === 'pro'}
       onChange={() => setPlan('pro')} label="Pro plan" />

// Sizes + supporting text
<Radio
  size="sm"
  label="Email notifications"
  supportingText="Get notified when a new lead matches your profile."
/>`

const RADIO_FIELD_OPTIONS = [
    { value: 'card', label: 'Credit card', supporting: 'Visa, Mastercard or Amex.' },
    { value: 'paypal', label: 'PayPal', supporting: 'Pay with your PayPal balance.' },
    { value: 'transfer', label: 'Bank transfer', supporting: 'Takes 1–3 business days.' },
]

const StatefulRadioField: React.FC<{ size?: RadioSize }> = ({ size = 'md' }) => {
    const [value, setValue] = useState('card')
    return (
        <div role="radiogroup" className="flex flex-col gap-spacing-xl">
            {RADIO_FIELD_OPTIONS.map((option) => (
                <Radio
                    key={option.value}
                    name={`payment-${size}`}
                    value={option.value}
                    size={size}
                    checked={value === option.value}
                    onChange={() => setValue(option.value)}
                    label={option.label}
                    supportingText={option.supporting}
                />
            ))}
        </div>
    )
}

export const RadioShowcase: React.FC = () => (
    <Section
        title="Radio (v2 component)"
        description={
            <>
                Generated from the same Figma <span className="font-semibold">Checkbox</span> set
                (ComponentSet <InlineCode>5:6526</InlineCode>), scoped to the{' '}
                <InlineCode>Type=Radio</InlineCode> variants. The circular twin of{' '}
                <InlineCode>Checkbox</InlineCode> — same API shape, a centered dot instead of a
                check, no <InlineCode>indeterminate</InlineCode>. Import v2 via{' '}
                <InlineCode>@trustoo/components/Radio/v2</InlineCode> (net-new — no v1 barrel yet).
            </>
        }>
        <div className="space-y-spacing-3xl">
            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['sm', 'md'] as RadioSize[]).map((size) => (
                        <div key={size} className="flex items-center gap-spacing-lg">
                            <Radio size={size} defaultChecked />
                            <Radio size={size} />
                            <span className="text-xs font-medium uppercase text-text-tertiary">
                                {size}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">States</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        Unchecked, checked, and <InlineCode>disabled</InlineCode>. Hover and{' '}
                        <InlineCode>:focus-visible</InlineCode> (tab onto a circle) are native.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Radio />
                    <Radio defaultChecked />
                    <Radio disabled />
                    <Radio disabled defaultChecked />
                </div>
            </div>

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">
                    With label &amp; supporting text
                </h3>
                <p className="text-xs text-text-tertiary">
                    A controlled single-select group — radios sharing a{' '}
                    <InlineCode>name</InlineCode> form one native group.
                </p>
                <div className="grid grid-cols-1 gap-spacing-3xl rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl sm:grid-cols-2">
                    <StatefulRadioField size="md" />
                    <StatefulRadioField size="sm" />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={RADIO_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>checked</InlineCode> /{' '}
                    <InlineCode>defaultChecked</InlineCode> + <InlineCode>onChange</InlineCode>,{' '}
                    <InlineCode>size</InlineCode> (sm · md), <InlineCode>label</InlineCode>,{' '}
                    <InlineCode>supportingText</InlineCode>, <InlineCode>name</InlineCode> /{' '}
                    <InlineCode>value</InlineCode>, and <InlineCode>disabled</InlineCode>. Renders a
                    native <InlineCode>&lt;input type=&quot;radio&quot;&gt;</InlineCode>; a provided{' '}
                    <InlineCode>label</InlineCode> is associated via{' '}
                    <InlineCode>htmlFor</InlineCode>.
                </p>
            </div>
        </div>
    </Section>
)
